import React, { forwardRef } from 'react';
import {
  TextField,
  TextFieldProps,
  FormHelperText,
  InputAdornment,
  useTheme,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';

export interface InputProps extends Omit<TextFieldProps, 'variant'> {
  variant?: 'outlined' | 'filled' | 'standard';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
  errorMessage?: string;
  successMessage?: string;
  helperText?: string;
}

export const Input = forwardRef<HTMLDivElement, InputProps>(
  (
    {
      type = 'text',
      variant = 'outlined',
      startIcon,
      endIcon,
      showPasswordToggle = false,
      error,
      errorMessage,
      successMessage,
      helperText,
      InputProps,
      sx,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const [showPassword, setShowPassword] = React.useState(false);

    const handleTogglePassword = () => {
      setShowPassword(!showPassword);
    };

    // Determine actual input type
    const inputType = showPasswordToggle 
      ? (showPassword ? 'text' : 'password')
      : type;

    // Build input adornments
    const buildStartAdornment = () => {
      if (!startIcon) return undefined;
      return (
        <InputAdornment position="start">
          {startIcon}
        </InputAdornment>
      );
    };

    const buildEndAdornment = () => {
      const elements = [];

      // Add custom end icon
      if (endIcon) {
        elements.push(endIcon);
      }

      // Add password toggle if enabled
      if (showPasswordToggle) {
        elements.push(
          <IconButton
            aria-label="toggle password visibility"
            onClick={handleTogglePassword}
            edge="end"
            size="small"
          >
            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        );
      }

      if (elements.length === 0) return undefined;

      return (
        <InputAdornment position="end">
          {elements.map((element, index) => (
            <React.Fragment key={index}>{element}</React.Fragment>
          ))}
        </InputAdornment>
      );
    };

    // Determine helper text to display
    const getHelperText = () => {
      if (error && errorMessage) {
        return errorMessage;
      }
      if (!error && successMessage) {
        return successMessage;
      }
      return helperText;
    };

    // Determine helper text color
    const getHelperTextColor = () => {
      if (error) return theme.palette.error.main;
      if (successMessage) return theme.palette.success.main;
      return theme.palette.text.secondary;
    };

    return (
      <div>
        <TextField
          ref={ref}
          type={inputType}
          variant={variant}
          error={error}
          InputProps={{
            startAdornment: buildStartAdornment(),
            endAdornment: buildEndAdornment(),
            ...InputProps,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: error 
                  ? theme.palette.error.main 
                  : successMessage 
                    ? theme.palette.success.main 
                    : theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: error 
                  ? theme.palette.error.main 
                  : successMessage 
                    ? theme.palette.success.main 
                    : theme.palette.primary.main,
              },
            },
            '& .MuiInputLabel-root': {
              '&.Mui-focused': {
                color: error 
                  ? theme.palette.error.main 
                  : successMessage 
                    ? theme.palette.success.main 
                    : theme.palette.primary.main,
              },
            },
            ...sx,
          }}
          {...props}
        />
        {getHelperText() && (
          <FormHelperText
            sx={{
              color: getHelperTextColor(),
              mt: 0.5,
              mx: 1.75,
              fontSize: '0.75rem',
            }}
          >
            {getHelperText()}
          </FormHelperText>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;